/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Button, BUTTON_FORMAT_SOFT, BUTTON_THEME_DEFAULT, BUTTON_THEME_PRIMARY, ErrorMessage } from '@squiz/sds';
import { COMPONENT_SERVICE_STRINGS } from 'apps/component-service/src/Constants/Strings.constants';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { getComponentService } from '../../../../../Services/component';
import { DELETE_ALL_COMPONENT_VERSIONS_DATA_IDS } from '../DeleteAllComponentVersions.constants';
import { DeleteAllComponentVersionsDialogProps } from './DeleteAllComponentVersionsDialog.props';

export const DeleteAllComponentVersionsDialog = (props: DeleteAllComponentVersionsDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { componentNamespace, componentName, includedInSets, close, setButtonKeys, setButtons } = props;

  const [isLoading, setLoading] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [error, setError] = useState(false);

  const labels = useMemo(() => {
    return {
      cancel: t(COMPONENT_SERVICE_STRINGS.cancel),
      delete: t(COMPONENT_SERVICE_STRINGS.delete),
      deleteAllComponentVersionsDescription: t(
        COMPONENT_SERVICE_STRINGS.deleteAllComponentVersionsDescription,
      ) as string,
      deleteError: t(COMPONENT_SERVICE_STRINGS.deleteAllComponentVersionsError) as string,
      removeFromAllSets: t(COMPONENT_SERVICE_STRINGS.removeFromAllSets) as string,
    };
  }, [t]);

  // Component service
  const componentService = getComponentService();

  const navigateToAllComponents = useCallback(
    (path: string): void => {
      navigate({ pathname: path });
    },
    [navigate],
  );

  // Component set details path handling
  const allComponentsPath = getCapabilityPath(`/all-components`);

  const deleteHandler = useCallback(() => {
    setLoading(true);
    setSubmit(false);
    lastValueFrom(
      componentService.deleteComponent({ deleteSchemas: true, webpath: `${componentNamespace}/${componentName}` }),
    )
      .then(() => {
        // Navigate back to all components page
        navigateToAllComponents(allComponentsPath);
        return close();
      })
      .catch((err) => {
        setSubmit(false);
        setError(true);
        return setLoading(false);
      });
  }, [allComponentsPath, close, componentName, componentNamespace, componentService, navigateToAllComponents]);

  useEffect(() => {
    // Cancel button
    const cancelButtonKey = `cancel`;
    const cancelButton = (
      <Button
        data-id={DELETE_ALL_COMPONENT_VERSIONS_DATA_IDS.cancelButton}
        disabled={isLoading}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );

    // Delete button
    const deleteButtonKey = `delete`;
    const deleteButton = (
      <Button
        data-id={DELETE_ALL_COMPONENT_VERSIONS_DATA_IDS.deleteButton}
        disabled={isLoading}
        isLoading={isLoading}
        onClick={(): void => setSubmit(true)}
        text={labels.delete}
        theme={BUTTON_THEME_PRIMARY}
      />
    );

    if (submit && !error) {
      setButtonKeys([deleteButtonKey]);
      setButtons([deleteButton]);
      deleteHandler();
    } else if (includedInSets > 0) {
      setButtonKeys([cancelButtonKey]);
      setButtons([cancelButton]);
    } else {
      setButtonKeys([cancelButtonKey, deleteButtonKey]);
      setButtons([cancelButton, deleteButton]);
    }
  }, [
    close,
    deleteHandler,
    error,
    includedInSets,
    isLoading,
    labels.cancel,
    labels.delete,
    setButtonKeys,
    setButtons,
    submit,
  ]);

  if (includedInSets > 0) {
    return <ErrorMessage message={labels.removeFromAllSets} />;
  }

  return (
    <div>
      <span>{labels.deleteAllComponentVersionsDescription}</span>
      {error && (
        <div className="sds-form-error" data-id={DELETE_ALL_COMPONENT_VERSIONS_DATA_IDS.modalError}>
          <ErrorMessage message={labels.deleteError} />
        </div>
      )}
    </div>
  );
};
